// Page entry. PRD 14 §1.2 / §7 permission matrix (final state, post-Slice 8):
//
//   Action                  | NO_PERMISSION | READ | WRITE | Root
//   ------------------------|:-------------:|:----:|:-----:|:----:
//   LNB entry visible       |       ✗       |  ✓   |   ✓   |  ✓    AdminNavigation.ts permission tag
//   View list / detail      |       ✗       |  ✓   |   ✓   |  ✓    NoPermissionGate (READ)
//   Register New Version    |       —       |  ✗   |   ✓   |  ✓    VersionListPanel button (WRITE)
//   Edit Version (DRAFT)    |       —       |  ✗   |   ✓   |  ✓    VersionDetailPanel button (WRITE + DRAFT)
//   Save Draft              |       —       |  ✗   |   ✓   |  ✓    VersionForm button (WRITE)
//   Publish                 |       —       |  ✗   |   ✓   |  ✓    VersionForm button (WRITE)
//   Delete Version (DRAFT)  |       —       |  ✗   |   ✗   |  ✓    VersionDetailPanel button (isRoot + DRAFT)
//   Manage Term Types       |       —       |  ✗   |   ✗   |  ✓    Header button + panel (isRoot)
//
// "—" = page is unreachable for NO_PERMISSION (gate blocks before any button
// renders). Server-side enforcement remains authoritative on every action.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Pencil, Plus, Settings, Trash2 } from 'lucide-react';

import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { InfoModal } from '@/components/feedback/InfoModal';
import { toast } from '@/components/feedback/useToast';

import { useAuthStore } from '@/app/store/auth.store';
import { TermsTypeManagerPanel } from '@/features/terms/components/TermsTypeManagerPanel';
import { TermsVersionList } from '@/features/terms/components/TermsVersionList';
import { VersionForm } from '@/features/terms/components/VersionForm';
import { VersionListSkeleton } from '@/features/terms/components/VersionListSkeleton';
import { VersionReadView } from '@/features/terms/components/VersionReadView';
import { useDeleteTermVersion } from '@/features/terms/hooks/use-term-mutations';
import {
  useTermTypesQuery,
  useTermVersionQuery,
  useTermVersionsQuery,
} from '@/features/terms/hooks/use-terms';
import { useTermsDetailStore } from '@/features/terms/store/terms-detail.store';
import type { TermType } from '@/features/terms/types/terms.types';
import { getTermsErrorMessage } from '@/features/terms/utils/error-message';
import { useWriteGuard } from '@/shared/hooks/useWriteGuard';
import { useRequirePermission } from '@/shared/hooks/useRequirePermission';

// Resolve a tab label: prefer the i18n key under `terms:typeNames.<slug>` for
// the 4 default seeds so EN locale renders English even though BE returned the
// Korean name; fall back to the wire `name` for admin-added types. Caveat: if
// Root renames a default seed via the type manager, the slug-keyed translation
// still wins over the renamed `name` — the rename is effectively visible in
// the type manager but not in the tab bar. Acceptable per Slice 0 Q9 (we
// prioritized cross-locale parity for the seeds over rename-fidelity).
function useTypeLabel() {
  const { t, i18n } = useTranslation('terms');
  return (type: TermType): string => {
    if (type.slug) {
      const key = `typeNames.${type.slug}`;
      if (i18n.exists(key, { ns: 'terms' })) return t(key);
    }
    return type.name;
  };
}

// Inline load-error + retry block. Mirrors the per-feature `TableError` pattern
// used by Notices / Disease Code / Health Encyclopedia (and the prototype
// `renderErrorState`): a failed load shows the message AND a Retry button wired
// to the React Query `refetch`, never a silent empty-state.
function TableError({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation('terms');
  return (
    <div
      role="alert"
      style={{
        margin: 16,
        padding: 16,
        border: '1px solid var(--destructive)',
        borderRadius: 'var(--radius-md)',
        background: '#FEE2E8',
        color: 'var(--destructive)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 16,
      }}
    >
      <span>{message}</span>
      <button type="button" className="btn btn-sm btn-outline" onClick={onRetry}>
        {t('list.retry')}
      </button>
    </div>
  );
}

function VersionListPanel({ activeType }: { activeType: TermType }) {
  const { t } = useTranslation('terms');
  const labelOf = useTypeLabel();
  const { data, isLoading, error, refetch } = useTermVersionsQuery(activeType.id);

  const openDetail = useTermsDetailStore((s) => s.openDetail);
  const openNew = useTermsDetailStore((s) => s.openNew);
  const { canWrite, denyProps } = useWriteGuard('PUBLICATION');

  const totalCount = data?.length ?? 0;

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--space-3)',
        }}
      >
        <div style={{ fontSize: 14, color: 'var(--muted-foreground)' }}>
          <span style={{ color: 'var(--foreground)' }}>
            {t('list.historyTitle', { name: labelOf(activeType) })}
          </span>{' '}
          {/* SHCT-683 follow-up — `list.totalCount` carries a <count> tag for the type
              panel, which renders it through <Trans>. Read from a plain `t()` here that
              tag reached the screen verbatim ("<count>23</count> total"). The header is
              a different visual anyway (prototype :16 emphasises the whole 총 N건, while
              the panel at :555 emphasises only the number), so it takes its own key
              instead of borrowing one whose markup does not belong to it. */}
          <span style={{ color: 'var(--foreground)', fontWeight: 600 }}>
            {t('list.historyTotalCount', { count: totalCount })}
          </span>
        </div>
        <button
          type="button"
          className="btn btn-default btn-primary"
          disabled={!canWrite}
          {...denyProps}
          onClick={() => openNew(activeType.id)}
        >
          <Plus size={14} />
          {t('list.actions.newVersion')}
        </button>
      </div>
      <div className="card">
        {isLoading ? (
          <VersionListSkeleton />
        ) : error ? (
          <TableError message={t('list.loadError')} onRetry={() => void refetch()} />
        ) : !data || data.length === 0 ? (
          <p
            style={{
              padding: '40px 0',
              textAlign: 'center',
              color: 'var(--muted-foreground)',
              margin: 0,
            }}
          >
            {t('list.empty')}
          </p>
        ) : (
          <TermsVersionList versions={data} onRowSelect={openDetail} />
        )}
      </div>
    </>
  );
}

function VersionDetailPanel({ typeId, versionId }: { typeId: number; versionId: number }) {
  const { t } = useTranslation('terms');
  const { data, isLoading, error, refetch } = useTermVersionQuery(typeId, versionId);
  const openList = useTermsDetailStore((s) => s.openList);
  const openEdit = useTermsDetailStore((s) => s.openEdit);
  const { canWrite, denyProps } = useWriteGuard('PUBLICATION');
  // PRD 14 §7 — Delete is Root-only (no PUBLICATION:DELETE level exists in
  // the catalog, so we gate by the auth-store `isRoot` flag).
  const isRoot = useAuthStore((s) => s.isRoot);

  const deleteMutation = useDeleteTermVersion();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // SHCT-702 — PRD 14 §8.2 shows a delete failure in a 팝업, not a toast.
  const [systemError, setSystemError] = useState<string | null>(null);

  // PRD 14 §2.2 No.13 + §3.4 — [입력/수정] is a DRAFT-only control. The two axes are
  // handled differently on purpose (SHCT-705/704):
  //   status axis  → PUBLISHED / ARCHIVED can NEVER be edited, so the button is HIDDEN.
  //                  Showing it disabled reads as "you lack permission" and sends the
  //                  operator to Root for an access grant that would change nothing;
  //                  the correct route is [새 버전 등록], named by the §8.3 notice.
  //   permission axis → a DRAFT the user may not write stays VISIBLE but disabled with
  //                  a tooltip, matching the prototype `guard()` rule that a
  //                  permission-gated control is never hidden.
  const isDraft = data?.status === 'DRAFT';
  const showEdit = Boolean(data) && isDraft;
  const canEdit = canWrite;
  const editTooltip = canWrite ? undefined : t('tooltips.noWritePermission');
  // PRD 14 §7 footnote — PUBLISHED / ARCHIVED versions are legally preserved
  // and cannot be deleted even by Root. Show Delete only for DRAFT (status axis).
  // Permission is a SEPARATE axis: non-Root keeps the button visible but disabled
  // (matches the prototype `guard()` — never hide a permission-gated control).
  const showDelete = Boolean(data) && isDraft;
  const canDelete = isRoot;
  const deleteTooltip = canDelete ? undefined : t('tooltips.rootOnly');

  const handleDeleteConfirm = async () => {
    if (!data || deleteMutation.isPending) return;
    try {
      await deleteMutation.mutateAsync({ typeId: data.typeId, versionId: data.id });
      toast.success(t('toasts.deleted'));
      setShowDeleteConfirm(false);
      openList();
    } catch (err) {
      // Close the confirm dialog on the way out: it used to stay open behind the failure notice, so
      // the operator was left staring at a [삭제] button for a delete that had just been refused.
      setShowDeleteConfirm(false);
      setSystemError(getTermsErrorMessage(err, t, 'errors.deleteFailed'));
    }
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--space-4)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            className="btn btn-sm btn-default btn-outline"
            onClick={openList}
          >
            <ArrowLeft size={14} />
            {t('detail.back')}
          </button>
          <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>{t('detail.title')}</h2>
        </div>
      </div>
      {isLoading ? (
        <div className="card" style={{ padding: 24 }}>
          <VersionListSkeleton />
        </div>
      ) : error || !data ? (
        <div className="card">
          <TableError message={t('detail.loadError')} onRetry={() => void refetch()} />
        </div>
      ) : (
        <VersionReadView version={data} />
      )}
      {/* SHCT-704 — on PUBLISHED / ARCHIVED both actions are hidden, so skip the row
          entirely instead of leaving an empty flex box holding its top margin. */}
      {data && (showEdit || showDelete) ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--space-2)',
            marginTop: 'var(--space-4)',
          }}
        >
          {showDelete ? (
            <span
              title={deleteTooltip}
              style={{ display: 'inline-flex', cursor: canDelete ? undefined : 'not-allowed' }}
            >
              <button
                type="button"
                className="btn btn-default btn-secondary"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={!canDelete || deleteMutation.isPending}
                style={canDelete ? undefined : { opacity: 0.5, pointerEvents: 'none' }}
              >
                <Trash2 size={14} />
                {t('detail.actions.delete')}
              </button>
            </span>
          ) : null}
          {/* Wrap in a span carrying the tooltip: native `title` does not show
              on a `disabled` button (browsers suppress its hover events), and the
              app has no generic `.btn:disabled` style, so the disabled state is
              made explicit here (opacity + not-allowed) — matching the prototype.
              `pointerEvents: none` on the button lets the hover reach the span. */}
          {showEdit ? (
            <span
              title={editTooltip}
              style={{ display: 'inline-flex', cursor: canEdit ? undefined : 'not-allowed' }}
            >
              <button
                type="button"
                className="btn btn-default btn-primary"
                disabled={!canEdit}
                {...denyProps}
                onClick={() => openEdit(data)}
                style={canEdit ? undefined : { opacity: 0.5, pointerEvents: 'none' }}
              >
                <Pencil size={14} />
                {t('detail.actions.edit')}
              </button>
            </span>
          ) : null}
        </div>
      ) : null}
      <ConfirmModal
        open={showDeleteConfirm}
        title={t('detail.confirmDelete.title')}
        message={t('detail.confirmDelete.message')}
        confirmLabel={t('detail.confirmDelete.confirm')}
        cancelLabel={t('detail.confirmDelete.cancel')}
        confirmVariant="destructive"
        loading={deleteMutation.isPending}
        loadingLabel={t('detail.confirmDelete.deleting')}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          if (deleteMutation.isPending) return;
          setShowDeleteConfirm(false);
        }}
      />
      <InfoModal
        open={systemError !== null}
        title={t('systemErrorModal.title')}
        body={systemError ?? ''}
        confirmLabel={t('systemErrorModal.confirm')}
        onClose={() => setSystemError(null)}
      />
    </>
  );
}

function TermsManagementView() {
  const { t } = useTranslation('terms');
  const labelOf = useTypeLabel();
  const { data: types, isLoading, error, refetch } = useTermTypesQuery();

  const mode = useTermsDetailStore((s) => s.mode);
  const currentVersionId = useTermsDetailStore((s) => s.currentVersionId);
  const activeTypeId = useTermsDetailStore((s) => s.activeTypeId);
  const setActiveTypeId = useTermsDetailStore((s) => s.setActiveTypeId);

  // Default the active tab to the first type once the list resolves. Also
  // recover if the previously-active type has been deleted (e.g. via the Slice
  // 7 type manager): fall back to the first remaining type.
  useEffect(() => {
    if (!types || types.length === 0) return;
    const stillExists = activeTypeId !== null && types.some((t) => t.id === activeTypeId);
    if (!stillExists) setActiveTypeId(types[0].id);
  }, [types, activeTypeId, setActiveTypeId]);

  const activeType = useMemo(
    () => types?.find((t) => t.id === activeTypeId) ?? null,
    [types, activeTypeId],
  );

  if (isLoading) {
    return (
      <div className="card" style={{ padding: 24 }}>
        <VersionListSkeleton />
      </div>
    );
  }
  if (error) {
    return (
      <div className="card">
        <TableError message={t('list.loadError')} onRetry={() => void refetch()} />
      </div>
    );
  }
  if (!types || types.length === 0) {
    return (
      <p
        style={{
          padding: '40px 0',
          textAlign: 'center',
          color: 'var(--muted-foreground)',
        }}
      >
        {t('list.empty')}
      </p>
    );
  }

  // Hide the tab bar while a form is open: switching tab mid-edit would
  // silently discard the draft. List/detail keep tabs visible so users can
  // jump between types.
  const showTabs = mode === 'list' || mode === 'detail';

  return (
    <>
      {showTabs ? (
        <div className="panel-tabs">
          {types.map((type) => (
            <button
              key={type.id}
              type="button"
              className={`panel-tab${type.id === activeTypeId ? ' active' : ''}`}
              onClick={() => setActiveTypeId(type.id)}
            >
              {labelOf(type)}
            </button>
          ))}
        </div>
      ) : null}
      {activeType && (mode === 'new' || mode === 'edit') ? (
        <VersionForm activeType={activeType} />
      ) : activeType && mode === 'detail' && currentVersionId !== null ? (
        <VersionDetailPanel typeId={activeType.id} versionId={currentVersionId} />
      ) : activeType ? (
        <VersionListPanel activeType={activeType} />
      ) : null}
    </>
  );
}

export function TermsManagementPage() {
  const { t } = useTranslation('terms');
  const canRead = useRequirePermission('PUBLICATION', 'READ');
  // PRD 14 §3.5 — type management is Root-only (no PUBLICATION:DELETE level in
  // the catalog). The button stays visible for non-Root admins but is disabled
  // with a tooltip (matches the prototype `guard()` — never hide a gated control).
  const isRoot = useAuthStore((s) => s.isRoot);
  const [typeManagerOpen, setTypeManagerOpen] = useState(false);

  if (!canRead) return null;

  return (
    <>
      <div className="content__header">
        <div>
          <h1 className="content__title">{t('title')}</h1>
        </div>
        <div className="content__actions">
          <span
            title={isRoot ? undefined : t('tooltips.rootOnly')}
            style={{ display: 'inline-flex', cursor: isRoot ? undefined : 'not-allowed' }}
          >
            <button
              type="button"
              className="btn btn-default btn-secondary"
              disabled={!isRoot}
              onClick={() => setTypeManagerOpen(true)}
              style={isRoot ? undefined : { opacity: 0.5, pointerEvents: 'none' }}
            >
              <Settings size={14} />
              {t('header.manageTypes')}
            </button>
          </span>
        </div>
      </div>
      <div className="content__body">
        <TermsManagementView />
      </div>
      {isRoot ? (
        <TermsTypeManagerPanel
          open={typeManagerOpen}
          onClose={() => setTypeManagerOpen(false)}
        />
      ) : null}
    </>
  );
}
